import { useMemo, useState } from 'react'
import { FlowMetaData, RealEstateCategory, TransactionType } from '../../../../@types/types'
import { ListingCopyTargetSegmentation } from '../../../../api/realEstate/listingTypeChangeApi'
import {
  getAvailableRealEstateTypes,
  getAvailableTransactions,
  getBusinessChildren,
} from '../../segmentationPage/segmentation-model'

type ListingTypeChangeTransaction = TransactionType | 'SHORT_TERM_ACCOMMODATION'
type ListingTypeChangeRealEstate = RealEstateCategory | 'BUSINESS'

type ListingTypeChangeSelection = {
  transaction?: ListingTypeChangeTransaction
  realEstate?: ListingTypeChangeRealEstate
  businessType?: RealEstateCategory
}

const isBusinessCategory = (category: RealEstateCategory) => getBusinessChildren().includes(category)

const toSegmentationCategory = (category: RealEstateCategory): RealEstateCategory =>
  category === RealEstateCategory.PRODUCTION_HALL ? RealEstateCategory.HANGAR : category

const toFlowCategory = (category: RealEstateCategory): RealEstateCategory =>
  category === RealEstateCategory.HANGAR ? RealEstateCategory.PRODUCTION_HALL : category

const selectionFromFlowMetadata = ({ transaction, category }: FlowMetaData): ListingTypeChangeSelection => {
  const segmentationCategory = toSegmentationCategory(category)

  if (category === RealEstateCategory.SHORT_TERM_ACCOMMODATION) {
    return { transaction: 'SHORT_TERM_ACCOMMODATION' }
  }

  if (isBusinessCategory(segmentationCategory)) {
    return { transaction, realEstate: 'BUSINESS', businessType: segmentationCategory }
  }

  return { transaction, realEstate: segmentationCategory }
}

export const useListingTypeChangeSelection = (flowMetadata: FlowMetaData) => {
  const [selection, setSelection] = useState<ListingTypeChangeSelection>(() => selectionFromFlowMetadata(flowMetadata))
  const sourceSegmentation = flowMetadata.segmentation

  const availableTransactions = getAvailableTransactions(sourceSegmentation)
  const availableRealEstateTypes = selection.transaction
    ? getAvailableRealEstateTypes(sourceSegmentation, selection.transaction)
    : []
  const availableBusinessTypes = selection.transaction
    ? getBusinessChildren(sourceSegmentation, selection.transaction)
    : []

  const canProceed = Boolean(
    selection.transaction &&
    (selection.transaction === 'SHORT_TERM_ACCOMMODATION' ||
      (selection.realEstate && selection.realEstate !== 'BUSINESS') ||
      selection.businessType)
  )

  const target = useMemo<ListingCopyTargetSegmentation | undefined>(() => {
    if (!canProceed || !selection.transaction) {
      return undefined
    }

    if (selection.transaction === 'SHORT_TERM_ACCOMMODATION') {
      return {
        segmentation: sourceSegmentation,
        transaction: TransactionType.RENT,
        category: RealEstateCategory.SHORT_TERM_ACCOMMODATION,
      }
    }

    const category = selection.businessType ?? selection.realEstate
    if (!category || category === 'BUSINESS') {
      return undefined
    }

    return {
      segmentation: sourceSegmentation,
      transaction: selection.transaction,
      category: toFlowCategory(category),
    }
  }, [canProceed, selection, sourceSegmentation])

  const selectTransaction = (transaction: ListingTypeChangeTransaction) => {
    setSelection((currentSelection) => {
      if (currentSelection.transaction === transaction) {
        return {}
      }

      return { transaction: transaction }
    })
  }

  const selectRealEstate = (realEstate: ListingTypeChangeRealEstate) => {
    setSelection((currentSelection) => {
      if (currentSelection.realEstate === realEstate) {
        return {
          transaction: currentSelection.transaction,
        }
      }

      return {
        transaction: currentSelection.transaction,
        realEstate,
      }
    })
  }

  const selectBusinessType = (businessType: RealEstateCategory) => {
    setSelection((currentSelection) => ({
      transaction: currentSelection.transaction,
      realEstate: 'BUSINESS',
      businessType: currentSelection.businessType === businessType ? undefined : businessType,
    }))
  }

  return {
    availableBusinessTypes,
    availableRealEstateTypes,
    availableTransactions,
    canProceed,
    selectedBusinessType: selection.businessType,
    selectedRealEstate: selection.realEstate,
    selectedTransaction: selection.transaction,
    selectBusinessType,
    selectRealEstate,
    selectTransaction,
    target,
  }
}
